import { ApiError } from "@/lib/api";

/**
 * The Fix My Error operation key (`PG-AI02`, ADR-0051).
 *
 * A sibling of `explain-operation.ts` with its own `pgfx_` prefix, because the
 * backend namespaces a reservation key on the use case: a key that crossed
 * surfaces would be answered with `IDEMPOTENCY_KEY_CONFLICT`, which is the
 * check working, not a bug to route around.
 */
export const FIX_OPERATION_KEY_PATTERN =
  /^pgfx_[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Errors after which the *next* deliberate attempt needs a fresh key, because
 * the backend has already closed the previous operation.
 */
const NEW_OPERATION_AFTER_ERROR_CODES = new Set([
  "AI_ERROR",
  "AI_UNAVAILABLE",
  "AI_GENERATION_PRIOR_FAILED",
  "AI_REPLAY_RESULT_UNAVAILABLE",
]);

export function createFixOperationKey(): string {
  return `pgfx_${crypto.randomUUID()}`;
}

/** One automatic retry for transport failures; API responses are authoritative. */
export function shouldRetryFixMutation(failureCount: number, error: unknown): boolean {
  return !(error instanceof ApiError) && failureCount < 1;
}

/**
 * An explicit retry keeps the same key unless the backend has said the prior
 * operation is finished with. Keeping it is what makes a retry free: the
 * reservation is already there, so nothing is charged twice.
 */
export function shouldReuseFixOperationKey(error: unknown): boolean {
  return !(
    error instanceof ApiError &&
    error.code !== undefined &&
    NEW_OPERATION_AFTER_ERROR_CODES.has(error.code)
  );
}
